import ContentIllustration from '../../components/ui/ContentIllustration';
import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import useAuthStore from '../../store/authStore';
import api from '../../services/api';
import { Copy, Check, Send, Loader2 } from 'lucide-react';

export default function GiftConfirmation() {
    const { user } = useAuthStore();
    const [searchParams] = useSearchParams();
    const codeParam = searchParams.get('code') || '';
    const sessionId = searchParams.get('session_id') || '';

    const [giftData, setGiftData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [copied, setCopied] = useState(false);
    const [error, setError] = useState(null);

    // Send email form
    const [sendEmail, setSendEmail] = useState('');
    const [sending, setSending] = useState(false);
    const [sendResult, setSendResult] = useState(null);

    useEffect(() => {
        if (!user || (!codeParam && !sessionId)) {
            setLoading(false);
            return;
        }
        let cancelled = false;
        const load = async (retries = 0) => {
            try {
                const param = codeParam
                    ? `code=${encodeURIComponent(codeParam)}`
                    : `session_id=${encodeURIComponent(sessionId)}`;
                const { data } = await api.get(`/gift/info.php?${param}`);
                if (cancelled) return;
                setGiftData(data.data);
                if (data.data.recipient_email) setSendEmail(data.data.recipient_email);
                setLoading(false);
            } catch (e) {
                if (cancelled) return;
                // Webhook may not have fired yet — retry up to 5 times (15s total)
                if (retries < 5 && sessionId) {
                    setTimeout(() => load(retries + 1), 3000);
                } else {
                    setError(e.response?.data?.message || 'Code cadeau introuvable');
                    setLoading(false);
                }
            }
        };
        load();
        return () => { cancelled = true; };
    }, [codeParam, sessionId, user]);

    const handleCopy = () => {
        navigator.clipboard.writeText(giftData?.code || codeParam);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const handleSend = async () => {
        if (!sendEmail) return;
        setSending(true);
        setSendResult(null);
        try {
            const { data } = await api.post('/gift/send.php', {
                code: giftData?.code || codeParam,
                email: sendEmail,
            });
            setSendResult({ ok: true, msg: data.data?.message || 'Envoyé !' });
        } catch (e) {
            setSendResult({ ok: false, msg: e.response?.data?.message || 'Erreur d\'envoi' });
        }
        setSending(false);
    };

    const dureeLabels = { '1m': '1 mois', '2m': '2 mois', '6m': '6 mois', '12m': '12 mois' };

    if (loading) {
        return (
            <div className="auth-page" style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Loader2 size={32} className="spin" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="auth-page" style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ maxWidth: 480, width: '100%', textAlign: 'center', padding: '40px 20px' }}>
                    <ContentIllustration name="gift" size={64} style={{ color: '#7c3aed', marginBottom: 16 }} />
                    <h1 style={{ fontSize: '1.5rem', marginBottom: 16 }}>Code cadeau</h1>
                    <p style={{ color: 'var(--text-muted)', marginBottom: 24 }}>{error}</p>
                    <Link to="/compte" className="btn btn-primary">Retour à mon compte</Link>
                </div>
            </div>
        );
    }

    return (
        <div className="auth-page" style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ maxWidth: 520, width: '100%', textAlign: 'center', padding: '40px 20px' }}>
                <ContentIllustration name="gift" size={64} style={{ color: '#7c3aed', marginBottom: 16 }} />
                <h1 style={{ fontSize: '1.5rem', marginBottom: 8 }}>Code cadeau généré !</h1>
                <p style={{ color: 'var(--text-muted)', marginBottom: 24 }}>
                    {giftData?.scope_label && <>
                        <strong>{giftData.scope_label}</strong> — {dureeLabels[giftData.duree] || giftData?.duree}
                    </>}
                </p>

                {/* Code display */}
                <div style={{
                    background: 'var(--section-alt)', border: '2px dashed var(--primary)',
                    borderRadius: 12, padding: '20px 24px', marginBottom: 24,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12,
                }}>
                    <span style={{ fontFamily: 'monospace', fontSize: '1.4rem', fontWeight: 700, letterSpacing: 2, color: 'var(--primary)' }}>
                        {giftData?.code || codeParam}
                    </span>
                    <button
                        onClick={handleCopy}
                        style={{
                            background: 'none', border: 'none', cursor: 'pointer',
                            color: copied ? '#10b981' : 'var(--text-muted)', transition: 'color 0.2s',
                        }}
                        title="Copier le code"
                    >
                        {copied ? <Check size={20} /> : <Copy size={20} />}
                    </button>
                </div>

                {/* Send by email */}
                <div style={{
                    background: 'var(--section-alt)', borderRadius: 12, padding: '16px 20px',
                    marginBottom: 24, textAlign: 'left',
                }}>
                    <p style={{ fontSize: '0.9em', fontWeight: 600, marginBottom: 10 }}>
                        <Send size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />
                        Envoyer par email
                    </p>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <input
                            type="email"
                            placeholder="Email du destinataire"
                            value={sendEmail}
                            onChange={(e) => setSendEmail(e.target.value)}
                            className="sub-modal-gift-input"
                            style={{ flex: 1 }}
                        />
                        <button
                            className="btn btn-primary"
                            onClick={handleSend}
                            disabled={sending || !sendEmail}
                            style={{ whiteSpace: 'nowrap', padding: '8px 16px' }}
                        >
                            {sending ? <Loader2 size={16} className="spin" /> : 'Envoyer'}
                        </button>
                    </div>
                    {sendResult && (
                        <p style={{ fontSize: '0.82em', marginTop: 8, color: sendResult.ok ? '#10b981' : '#ef4444' }}>
                            {sendResult.msg}
                        </p>
                    )}
                </div>

                <p style={{ fontSize: '0.85em', color: 'var(--text-muted)', marginBottom: 24 }}>
                    Le destinataire devra créer un compte (ou se connecter) puis entrer ce code pour activer son abonnement.
                </p>

                <Link to="/compte" className="btn btn-primary">Retour à mon compte</Link>
            </div>
        </div>
    );
}
